import {
    buildSuccessItem,
    calcProgress,
    generateFileId,
    mergeUploadOptions,
    resolveUploadParams,
    uploadFile,
} from './uploadFile';
import {validateFiles} from './validators';
import {UploadError} from '@/types/file';
import type {
    SubmitResult,
    UploadFileItem,
    UploadFileTypeOption,
    UploadOptions,
    UploadSource,
} from '@/types/file';
import type {ProjectId} from '@/types';

export interface FileSelectOptions {
    /** 已获取到的文件列表；传入时跳过文件选择弹窗 */
    files?: File[] | FileList;
    type: UploadFileTypeOption;
    /** 覆盖默认配置（如 allowedExtensions/maxSizeByte/maxCount/multiple） */
    options?: UploadOptions;
    onSuccess: (files: File[]) => void;
    onError?: (payload: {files: File[]; error: UploadError}) => void;
    /** 用户取消文件选择（关闭系统文件对话框）时触发 */
    onCancel?: () => void;
}

/**
 * 文件选择工具函数
 */
export function selectFiles({
    files: directFiles,
    type,
    options: userOptions,
    onSuccess,
    onError,
    onCancel,
}: FileSelectOptions) {
    const options = mergeUploadOptions(type, userOptions);
    if (directFiles) {
        const files = Array.from(directFiles);
        if (!files.length) {
            onCancel?.();
            return;
        }

        const error = validateFiles(files, options, type);
        if (error) {
            onError?.({files, error});
            return;
        }

        onSuccess(files);
        return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    // 通过样式隐藏 input，避免被用户误触
    Object.assign(input.style, {
        position: 'fixed',
        left: '-9999px',
        top: '0',
        opacity: '0',
        pointerEvents: 'none',
    });

    if (options.allowedExtensions?.length) {
        input.accept = options.allowedExtensions.map((e) => `.${e}`).join(',');
    }
    input.multiple = options.multiple ?? false;

    // 终态后清空 handler，断开 input ↔ 回调闭包链，避免持有外部 UI 引用
    const cleanup = () => {
        input.onchange = null;
        input.oncancel = null;
        input.remove();
    };

    input.onchange = () => {
        const files = Array.from(input.files ?? []);
        cleanup();
        if (!files.length) {
            return;
        }

        const error = validateFiles(files, options, type);
        if (error) {
            onError?.({files, error});
            return;
        }

        onSuccess(files);
    };

    // 现代浏览器（Chrome 113+/Firefox 91+/Safari 16.4+）在用户关闭文件对话框时触发 cancel
    input.oncancel = () => {
        cleanup();
        onCancel?.();
    };

    document.body.appendChild(input);
    input.click();
}

export interface SelectAndUploadParams {
    /** 已获取到的文件列表；传入时跳过文件选择弹窗 */
    files?: File[] | FileList;
    /** 上传类型 */
    type: UploadFileTypeOption;
    /** 业务场景 */
    scene: UploadSource;
    /** 上传配置（multiple/maxCount 等） */
    options?: UploadOptions;
    /** 额外请求头 */
    headers?: Record<string, string>;
    /** 当前项目 ID；无项目上传场景不传 */
    projectId?: ProjectId;
    /** 文件状态变化回调，每次推送完整列表快照 */
    onUpload?: (items: UploadFileItem[]) => void;
}

/**
 * 选择文件并上传
 *
 * 文件级并发由 options.concurrency（或对应类型默认值）控制，避免同源连接池被打满。
 * 单文件失败不阻断其他文件，最终通过 SubmitResult 返回成功与失败列表。
 */
export function selectAndUploadFiles(params: SelectAndUploadParams): Promise<SubmitResult> {
    const {files: directFiles, type, scene, options, headers, projectId, onUpload} = params;
    const mergedOptions = mergeUploadOptions(type, options);
    const fileConcurrency = Math.max(1, mergedOptions.concurrency);

    return new Promise((resolve) => {
        let items: UploadFileItem[] = [];
        // rAF 合并高频通知：进度事件每分片触发一次
        let rafId: number | null = null;
        const scheduleNotify = () => {
            if (rafId !== null || !onUpload) {
                return;
            }
            rafId = requestAnimationFrame(() => {
                rafId = null;
                onUpload(items);
            });
        };
        const patchItem = (id: string, patch: Partial<UploadFileItem>) => {
            items = items.map((it) => (it.id === id ? {...it, ...patch} : it));
            scheduleNotify();
        };

        selectFiles({
            files: directFiles,
            type,
            options,
            onSuccess: async (files) => {
                const originalItems: UploadFileItem[] = files.map((file) => ({
                    id: generateFileId(),
                    file,
                    progress: 0,
                    status: 'READY',
                }));
                items = originalItems;
                onUpload?.(items);

                const total = originalItems.length;
                const results = new Array<PromiseSettledResult<UploadFileItem>>(total);
                let cursor = 0;
                const runner = async () => {
                    while (cursor < total) {
                        const i = cursor++;
                        const item = originalItems[i];
                        try {
                            patchItem(item.id, {status: 'UPLOADING', progress: 0});
                            const {fileType, options: finalOptions} = resolveUploadParams(
                                item.file,
                                type,
                                options,
                            );
                            const response = await uploadFile({
                                file: item.file,
                                options: finalOptions,
                                scene,
                                fileType,
                                projectId,
                                headers,
                                onProgress: (completed, totalBytes) => {
                                    patchItem(item.id, {
                                        progress: calcProgress(completed, totalBytes),
                                    });
                                },
                                onRiskProcessing: (resp) => {
                                    patchItem(item.id, {
                                        progress: 100,
                                        response: resp,
                                        riskStatus: resp.riskStatus,
                                        taskId: resp.taskId,
                                        coverUrl: resp.coverUrl,
                                    });
                                },
                            });
                            results[i] = {
                                status: 'fulfilled',
                                value: buildSuccessItem(item, response),
                            };
                        } catch (reason) {
                            results[i] = {status: 'rejected', reason};
                        }
                    }
                };
                await Promise.all(
                    Array.from({length: Math.min(fileConcurrency, total)}, () => runner()),
                );

                const succeeded: UploadFileItem[] = [];
                const failed: UploadFileItem[] = [];
                const finalItems = results.map((r, i) => {
                    if (r.status === 'fulfilled') {
                        succeeded.push(r.value);
                        return r.value;
                    }
                    const error =
                        r.reason instanceof UploadError
                            ? r.reason
                            : new UploadError('UPLOAD', 'UPLOAD_REQUEST_FAILED', r.reason);
                    const failedItem: UploadFileItem = {
                        ...originalItems[i],
                        status: 'ERROR',
                        error,
                    };
                    failed.push(failedItem);
                    return failedItem;
                });

                // 终态同步推送：cancel 排队的 rAF 后直接通知，确保 resolve 前消费方拿到最终快照
                if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                }
                onUpload?.(finalItems);
                resolve({succeeded, failed});
            },
            onError: ({files, error}) => {
                // 校验失败：构造 failed 列表，与 hook submit 行为一致
                items = files.map((file) => ({
                    id: generateFileId(),
                    file,
                    progress: 0,
                    status: 'ERROR',
                    error,
                }));
                onUpload?.(items);
                resolve({succeeded: [], failed: items});
            },
            onCancel: () => {
                // 用户取消选择，返回空结果，不触发 onUpload
                resolve({succeeded: [], failed: []});
            },
        });
    });
}
